import { createApi } from "@reduxjs/toolkit/query/react";
import { dynamicBaseQuery } from "../badRequestHandler/BadRequestHandler";

export type TicketClickToExpandParams = {
  graph_name: string;
  value?: string;
  start_date: string;
  end_date: string;
  team_id: string | number;
  page?: number;
  page_size?: number;
  email_type?: string;
  workflow_id?: string | null;
  selected_date?: string;
  tag_id?: string | number;
  priority?: string;
  date?: string;
  status_type?: string;
  status_id?: string | number;
};

export const TicketDashboard = createApi({
  reducerPath: "TicketDashboard",
  baseQuery: dynamicBaseQuery,
  refetchOnMountOrArgChange: true,
  endpoints: (builder) => ({
    // ── Main dashboard query ───────────────────────────────────────────────
    // dashboardType: "overview" | "performance" | "analytics" | "volume"
    ticketDashboard: builder.query({
      query: ({
        dashboardType,
        selectedTeam,
        selectedWorkFlow,
        graphStartDate,
        graphEndDate,
      }: {
        dashboardType: string;
        selectedTeam: string | number;
        selectedWorkFlow?: string;
        graphStartDate: string;
        graphEndDate: string;
      }) => {
        const params: Record<string, string | number> = {
          start_date: graphStartDate,
          end_date: graphEndDate,
          team_id: selectedTeam,
        };
        if (selectedWorkFlow) params.workflow_id = selectedWorkFlow;

        return {
          url: `v1/common/ticketDashboard/${dashboardType}/`,
          method: "GET",
          params,
        };
      },
    }),

    // ── Click-to-expand query (one API, dynamic graph_name + value) ────────
    // graph_name examples:
    //   "tickets_by_status_type"  → value: "active" | "closed" | "wip" | "reopen"
    //   "ticket_volume_by_hour"   → value: "09" (zero-padded hour)
    //   "tickets_by_channel"      → value: "email" (raw channel string)
    //   "top_five_dispositions"   → value: "<disposition_id>"
    //   "tickets_bar_graph"       → value: "2024-01-15:pending" | "2024-01-15:resolved"
    getTicketClickToExpand: builder.query({
      query: ({
        graph_name,
        value,
        start_date,
        end_date,
        team_id,
        page,
        page_size,
        email_type,
        workflow_id,
        selected_date,
        tag_id,
        priority,
        date,
        status_type,
        status_id,
      }: TicketClickToExpandParams) => {
        const params: Record<string, string | number> = {
          graph_name,
          start_date,
          end_date,
          team_id,
          page: page ?? 1,
          page_size: page_size ?? 20,
        };
        if (value != null && value !== "") {
          params.value = value;
        }
        if (workflow_id != null && workflow_id !== "") {
          params.workflow_id = workflow_id;
        }
        if (email_type != null && email_type !== "") {
          params.email_type = email_type;
        }
        if (selected_date) {
          params.selected_date = selected_date;
        }
        if (tag_id != null && tag_id !== "") {
          params.tag_id = tag_id;
        }
        if (priority) {
          params.priority = priority;
        }
        if (date) {
          params.date = date;
        }
        if (status_type) {
          params.status_type = status_type;
        }
        if (status_id != null && status_id !== "") {
          params.status_id = status_id;
        }
        return {
          url: `v1/common/ticketDashboard/click-to-expand/`,
          method: "GET",
          params,
        };
      },
    }),
  }),
});

export const { useTicketDashboardQuery, useGetTicketClickToExpandQuery } =
  TicketDashboard;
